import { useState } from "react";
import {
  Rocket,
  Target,
  TrendingUp,
  Users,
  Sparkles,
  MessageCircle,
  Hash,
  Check,
  Star,
  Clock,
  Calendar,
} from "lucide-react";
import config from "../../config";

const GoViralStrategyLanding = ({
  isAuthenticated,
  onSignup,
  onSetTargetAudienceModalOpen,
}: any) => {
  const [selectedPlan, setSelectedPlan] = useState("viral");

  return (
    <div className="bg-gradient-to-b from-purple-50 to-white min-h-screen">
      {/* Hero Section */}
      <header className="pt-10 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-12">
              <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                For TikTok, Instagram Reels & YouTube Shorts Creators
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
                  Daily AI Viral Strategist
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Your one-stop daily assistant for fresh video ideas, trending
                insights, and viral growth strategies tailored for TikTok,
                Instagram Reels, and YouTube Shorts.
              </p>
              <div className="flex flex-wrap space-x-4 mb-8">
                <div className="flex items-center space-x-2 bg-white shadow-sm rounded-lg px-3 py-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  <span className="text-sm">Recent Trends</span>
                </div>
                <div className="flex items-center space-x-2 bg-white shadow-sm rounded-lg px-3 py-2 mb-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  <span className="text-sm">Audience Insights</span>
                </div>
                <div className="flex items-center space-x-2 bg-white shadow-sm rounded-lg px-3 py-2 mb-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <span className="text-sm">Fresh Ideas Daily</span>
                </div>
              </div>
              <button
                onClick={(e: any) => {
                  e.preventDefault();
                  if (!isAuthenticated) {
                    onSignup();
                  } else {
                    onSetTargetAudienceModalOpen(true);
                  }
                }}
                className="inline-block px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full font-medium shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Start My Daily Strategy
              </button>
            </div>
            <div className="w-full md:w-1/2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-200 to-pink-100 rounded-2xl transform rotate-2"></div>
                <div className="relative bg-white p-6 rounded-2xl shadow-xl">
                  <img
                    src={`${process.env.PUBLIC_URL}/example.webp`}
                    alt="Daily Report Form Example"
                    className="rounded-lg shadow-sm w-full"
                  />
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="bg-purple-50 p-3 rounded-lg flex flex-col items-center">
                      <Target className="h-6 w-6 text-purple-600 mb-2" />
                      <p className="text-xs text-center text-gray-600">
                        Audience Insights
                      </p>
                    </div>
                    <div className="bg-pink-50 p-3 rounded-lg flex flex-col items-center">
                      <Hash className="h-6 w-6 text-pink-600 mb-2" />
                      <p className="text-xs text-center text-gray-600">
                        Fresh Content Ideas
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg flex flex-col items-center">
                      <Clock className="h-6 w-6 text-purple-600 mb-2" />
                      <p className="text-xs text-center text-gray-600">
                        Generate Daily
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              How GoViralStrategy Works – In a Day, Every Day
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three simple steps to unlock your creator potential with daily
              insights for TikTok, Instagram Reels, and YouTube Shorts
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Fill Out a Quick Daily Form",
                description:
                  "Share a brief update on your current focus, mood, and goals for today. No heavy technical setup—just answer a few quick questions and let our AI do the rest!",
              },
              {
                icon: Sparkles,
                title: "Daily AI Magic at Your Fingertips",
                description:
                  "Each day, our AI scans the latest trends, analyzes successful creators, and delivers a personalized 'viral boost' report with fresh recommendations.",
              },
              {
                icon: Rocket,
                title: "Implement & Succeed",
                description:
                  "Start your day with actionable insights: dynamic audience profiles, fresh viral hook concepts, trending keywords, and a tailored engagement plan.",
              },
            ].map((step, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500 transform hover:-translate-y-2"
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <step.icon className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-center">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              What You'll Receive Daily
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fresh, actionable insights every day to keep your content dynamic
              and ahead of trends
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <div className="space-y-8">
                {[
                  {
                    icon: Calendar,
                    title: "Fresh Daily Report",
                    description:
                      "Receive a new strategy report every day to keep your content dynamic and ahead of trends.",
                  },
                  {
                    icon: Target,
                    title: "Updated Audience Insights",
                    description:
                      "Understand your target audience with refined daily analysis covering interests, challenges, and trending topics.",
                  },
                  {
                    icon: Sparkles,
                    title: "Viral Hook & Content Ideas",
                    description:
                      "Discover unique video concepts and caption templates that inspire engagement every time you post.",
                  },
                  {
                    icon: Hash,
                    title: "Trending Hashtags & Keywords",
                    description:
                      "Get daily curated lists of hashtags and keywords to maintain your competitive edge.",
                  },
                  {
                    icon: MessageCircle,
                    title: "Engagement Tips",
                    description:
                      "Generated actionable tips on how to interact with other creators and your fans for maximum impact.",
                  },
                ].map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded-xl transform rotate-3"></div>
                <div className="relative bg-white p-6 rounded-xl shadow-xl">
                  <div className="bg-purple-50 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-lg mb-4 flex items-center">
                      <Target className="h-5 w-5 text-purple-600 mr-2" />
                      Today's Target Audience Insights
                    </h4>
                    <div className="space-y-3 text-sm">
                      <p>
                        <span className="font-medium">Primary Audience:</span>{" "}
                        Fashion-forward women, 18-24, college students and young
                        professionals
                      </p>
                      <p>
                        <span className="font-medium">Today's Trends:</span>{" "}
                        Spring transition outfits, sustainable fashion, thrift
                        flips, "get ready with me" content
                      </p>
                      <p>
                        <span className="font-medium">
                          Current Pain Points:
                        </span>{" "}
                        Finding affordable yet trendy outfits for changing
                        weather, styling pieces multiple ways
                      </p>
                      <p>
                        <span className="font-medium">
                          Today's Engagement Tips:
                        </span>{" "}
                        Most active between 3-6pm, high engagement on styling
                        tutorials and OOTD content
                      </p>
                    </div>
                  </div>

                  <div className="bg-pink-50 rounded-lg p-6">
                    <h4 className="font-semibold text-lg mb-4 flex items-center">
                      <Sparkles className="h-5 w-5 text-pink-600 mr-2" />
                      Today's Viral Hook Ideas
                    </h4>
                    <div className="space-y-3 text-sm">
                      <p>
                        "
                        <span className="font-medium">
                          3 spring transition outfits that are getting me
                          compliments everywhere I go 👀
                        </span>
                        "
                      </p>
                      <p>
                        "
                        <span className="font-medium">
                          POV: How I turned yesterday's $20 thrift find into
                          today's viral fashion trend 🔥
                        </span>
                        "
                      </p>
                      <p>
                        "
                        <span className="font-medium">
                          The styling hack fashion TikTok is obsessing over
                          TODAY... #fashionhack
                        </span>
                        "
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results/Testimonials Section */}
      <section id="results" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Success Stories from Daily Users
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how other creators have transformed their growth using daily
              insights from GoViralStrategy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                name: "Alex K.",
                handle: "@fashionboss",
                niche: "Fashion",
                result: "10K to 45K followers in 60 days",
                quote:
                  "With my daily strategy, my content is always fresh. I went from sporadic posting to daily hits – one viral video led to 2M views in just a week!",
                color: "from-purple-500 to-purple-600",
              },
              {
                name: "Jamie T.",
                handle: "@foodie_adventures",
                niche: "Food Content",
                result: "First brand deal within 30 days",
                quote:
                  "Daily insights changed my game. The consistent new ideas connected me with top creators, landing my first major brand deal.",
                color: "from-pink-500 to-pink-600",
              },
              {
                name: "Taylor M.",
                handle: "@fitness_daily",
                niche: "Fitness",
                result: "3x higher engagement rate",
                quote:
                  "The daily viral hooks keep my intros fresh. My retention rate and engagement are better than ever!",
                color: "from-purple-500 to-purple-600",
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 flex flex-col"
              >
                <div className="flex items-center mb-4">
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">
                      {testimonial.handle}
                    </p>
                  </div>
                </div>
                <div className="mb-4">
                  <div
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${testimonial.color}`}
                  >
                    {testimonial.niche}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 flex-grow">
                  "{testimonial.quote}"
                </p>
                <div className="mt-auto">
                  <div className="flex items-center">
                    <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
                    <span className="font-medium text-green-600">
                      {testimonial.result}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white p-8 rounded-xl shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  stat: "87%",
                  label:
                    "of creators saw follower growth within 30 days of daily use",
                  icon: TrendingUp,
                },
                {
                  stat: "3.2x",
                  label:
                    "average increase in content engagement with daily strategies",
                  icon: Users,
                },
                {
                  stat: "74%",
                  label:
                    "received partnership opportunities after consistently implementing our daily tips",
                  icon: Sparkles,
                },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-2">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold mb-2">{item.stat}</h3>
                  <p className="text-white/80">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-16 px-4 bg-gradient-to-b from-white to-purple-50"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Daily Plans for Creators
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the daily plan that matches your growth goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Daily Basic",
                price: "$9.99",
                color: "border-gray-200",
                highlight: false,
                features: [
                  "30-31 Daily Reports (one for each day)",
                  "Daily Audience Insights",
                  "Daily Viral Hook Ideas",
                  "Trending Hashtags & Keywords",
                  "Basic Content Calendar",
                ],
                button: {
                  text: "Get Started",
                  color: "bg-gray-600 hover:bg-gray-700",
                },
                id: "basic",
              },
              {
                title: "Daily Viral",
                price: "$19.99",
                color: "border-purple-500",
                highlight: true,
                features: [
                  "60-62 Daily Reports (two per day)",
                  "Everything in Daily Basic +",
                  "Detailed Engagement Strategy",
                  "Competitor Analysis",
                  "Twice Daily Trend Alerts",
                  "Caption Templates",
                ],
                button: {
                  text: "Get Started",
                  color:
                    "bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600",
                },
                id: "viral",
              },
              {
                title: "Daily Pro",
                price: "$49.99",
                color: "border-gray-200",
                highlight: false,
                features: [
                  "120+ Daily Reports (multiple insights throughout the day)",
                  "Everything in Daily Viral +",
                  "Cross-Platform Sharing Plan",
                  "Partnership Opportunity Finder",
                  "Monetization Strategy",
                  "Priority Support",
                ],
                button: {
                  text: "Get Started",
                  color: "bg-gray-600 hover:bg-gray-700",
                },
                id: "pro",
              },
            ].map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 ${
                  selectedPlan === plan.id
                    ? "transform scale-105 shadow-xl"
                    : ""
                } ${
                  plan.highlight
                    ? "border-2 border-purple-500"
                    : "border border-gray-200"
                }`}
                onMouseEnter={() => setSelectedPlan(plan.id)}
              >
                {plan.highlight && (
                  <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-center py-2 text-sm font-medium">
                    MOST POPULAR
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-xl font-semibold mb-4 text-center">
                    {plan.title}
                  </h3>
                  <div className="text-center mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e: any) => {
                      e.preventDefault();
                      if (!isAuthenticated) {
                        onSignup(plan.id);
                      } else {
                        onSetTargetAudienceModalOpen(true);
                      }
                    }}
                    className={`w-full py-3 px-6 rounded-lg text-white font-medium transition-all duration-300 ${plan.button.color}`}
                  >
                    {plan.button.text}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 text-gray-500">
            Cancel anytime – no long-term commitments.
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              How Daily Strategies Amplify Your Creator Potential
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start each day with a creative edge – powered by daily insights
              from GoViralStrategy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Sparkles,
                title: "Fresh Inspiration Every Day",
                description:
                  "By generating and receiving daily insights, you never run out of ideas. Every morning, you're equipped with the latest trends and creative hooks that keep your content exciting.",
              },
              {
                icon: Users,
                title: "Maximized Engagement",
                description:
                  "With engagement strategies and updated audience profiles delivered daily, you can build and sustain genuine connections with your followers.",
              },
              {
                icon: Clock,
                title: "Time-Efficiency",
                description:
                  "Our streamlined daily reports remove the guesswork from content planning. Spend less time strategizing and more time creating, knowing your daily AI strategist has got your back.",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500 transform hover:-translate-y-2"
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                question:
                  "How is this different from using ChatGPT or other AI tools?",
                answer:
                  "GoViralStrategy is specifically trained on short-form video trends, audience data, and creator success patterns. Unlike general AI tools, we focus exclusively on what works for TikTok, Reels, and Shorts, providing daily actionable strategies, not just generic ideas.",
              },
              {
                question: "Do I need to be tech-savvy to use GoViralStrategy?",
                answer:
                  "Not at all! We've designed GoViralStrategy to be super easy to use. Just fill out a simple daily form about your content focus and goals, and we'll handle the complex analysis behind the scenes. No technical knowledge required.",
              },
              {
                question: "Will this work for any content niche?",
                answer:
                  "Yes! Our AI has been trained on successful creator data across all major content categories including beauty, fashion, fitness, food, lifestyle, education, comedy, finance, gaming, and more. Whatever your niche, we can help optimize your daily strategy.",
              },
              {
                question: "How soon will I see results?",
                answer:
                  "Most creators see improved engagement within the first 2-3 days of implementing our daily recommendations. For follower growth, results typically become significant after 2-4 weeks of consistent application of the daily strategies.",
              },
              {
                question: "Can I cancel anytime?",
                answer:
                  "Absolutely! There are no long-term commitments. You can cancel your subscription at any time with just a few clicks in your account settings.",
              },
            ].map((faq, index) => (
              <div key={index} className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-lg mb-2">{faq.question}</h4>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="get-started"
        className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Start Your Daily Creator Success Journey?
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Join thousands of creators who are growing their audience every day
            with fresh insights from GoViralStrategy
          </p>
          <a
            href="#form"
            onClick={() => {
              if (!isAuthenticated) {
                onSignup("viral");
              } else {
                onSetTargetAudienceModalOpen(true);
              }
            }}
            className="inline-block px-8 py-4 bg-white text-purple-600 rounded-full font-medium shadow-lg hover:shadow-xl hover:bg-gray-100 transition-all duration-300"
          >
            Get My First Daily Strategy Now
          </a>
          <div className="mt-8 flex justify-center space-x-6">
            <div className="flex items-center">
              <Star className="h-5 w-5 text-yellow-300 mr-1" />
              <Star className="h-5 w-5 text-yellow-300 mr-1" />
              <Star className="h-5 w-5 text-yellow-300 mr-1" />
              <Star className="h-5 w-5 text-yellow-300 mr-1" />
              <Star className="h-5 w-5 text-yellow-300 mr-1" />
            </div>
            <p className="text-white/90">
              Rated 4.9/5 from over 1,200 daily users
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Rocket className="h-6 w-6 text-purple-400" />
                <span className="font-bold text-xl">GoViralStrategy</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Your daily AI-powered growth strategies for short-form video
                creators
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href={config.links.affiliateProgram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Affiliate Program
                  </a>
                </li>
                <li>
                  <a
                    href={config.links.newsletter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Trend Reports
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href={config.links.legal}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href={config.links.legal}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#faq"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    FAQ
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${config.emails.support}`}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 mt-8 border-t border-gray-800 text-sm text-gray-400 flex flex-col md:flex-row justify-between items-center">
            <p>
              &copy; 2025 GoViralStrategy. All rights reserved. |{" "}
              <a href="https://sprunkid.com/">Sprunkid Partner</a>
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a
                href="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Privacy
              </a>
              <a
                href={config.links.legal}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Terms
              </a>
              <a
                href="/legal/cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GoViralStrategyLanding;
